import { Add, DeleteOutlineOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { enqueueSnackbar } from 'notistack';

import { DivisaoAutoComplete, GrupoAutoComplete, SecaoAutoComplete, SubgrupoAutoComplete } from 'components/autocompletes';
import { DreConfiguracao } from 'pages/financeiro/dre/configuracao/types';

export function DreConfigEstruturaMercadologica() {
    const { values, setFieldValue, errors } = useFormikContext<DreConfiguracao>();

    return (
        <FieldArray name="dreMercadologicaConfiguracaoCategorizacao">
            {({ push, remove }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: 'secondary.main', fontSize: '16px' }}>Estrutura mercadológica</Typography>

                        <Button size="small" variant="text" onClick={() => push({})} startIcon={<Add />}>
                            Adicionar
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 240, overflow: 'scroll', paddingTop: 0.75 }}>
                        {values.dreMercadologicaConfiguracaoCategorizacao.map((estrutura, index) => {
                            const isOnly = values.dreMercadologicaConfiguracaoCategorizacao.length === 1;
                            const isLast = index === values.dreMercadologicaConfiguracaoCategorizacao.length - 1;

                            return (
                                <Box
                                    sx={{
                                        'display': 'flex',
                                        'alignItems': 'center',
                                        'gap': 1.5,
                                        'borderColor': 'grey.300',
                                        'borderBottomStyle': 'solid',
                                        'borderBottomWidth': isLast ? 0 : 1,
                                        'paddingBottom': isLast ? 0 : 1.5,
                                        '& > .MuiBox-root': {
                                            flex: 1,
                                            gap: 1.5,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        },
                                    }}
                                    key={index}
                                >
                                    <Box>
                                        <DivisaoAutoComplete
                                            size="small"
                                            value={estrutura.divisao}
                                            error={getIn(errors, `dreMercadologicaConfiguracaoCategorizacao.${index}.divisao`)}
                                            onChange={(e, value) => {
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.divisao`, value);
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.secao`, null);
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.grupo`, null);
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.subgrupo`, null);
                                            }}
                                        />

                                        <SecaoAutoComplete
                                            size="small"
                                            value={estrutura.secao}
                                            optionStringTemplate="<strong>{dsSecao}</strong><br>Divisão {dsDivisao}"
                                            error={getIn(errors, `dreMercadologicaConfiguracaoCategorizacao.${index}.secao`)}
                                            onChange={(e, value) => {
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.secao`, value);
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.grupo`, null);
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.subgrupo`, null);
                                            }}
                                            requestOptions={{
                                                url: '/gestao/secao',
                                                sort: [{ property: 'dsSecao', direction: 'ASC' }],
                                                columns: 'idSecao,dsSecao,cdSecao,dsDivisao',
                                                limit: 100,
                                                filter: [
                                                    {
                                                        property: 'idDivisao',
                                                        operator: 'eq',
                                                        value: getIn(values, `dreMercadologicaConfiguracaoCategorizacao.${index}.divisao.idDivisao`),
                                                    },
                                                ],
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <GrupoAutoComplete
                                            size="small"
                                            value={estrutura.grupo}
                                            error={getIn(errors, `dreMercadologicaConfiguracaoCategorizacao.${index}.grupo`)}
                                            optionStringTemplate="<strong>{dsGrupo}</strong><br>Seção {dsSecao}<br>Divisão {dsDivisao}"
                                            onChange={(e, value) => {
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.grupo`, value);
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.subgrupo`, null);
                                            }}
                                            requestOptions={{
                                                url: '/gestao/grupo',
                                                sort: [{ property: 'dsGrupo', direction: 'ASC' }],
                                                columns: 'idGrupo,dsGrupo,dsSecao,dsDivisao',
                                                limit: 100,
                                                filter: [
                                                    {
                                                        property: 'idDivisao',
                                                        operator: 'eq',
                                                        value: getIn(values, `dreMercadologicaConfiguracaoCategorizacao.${index}.divisao.idDivisao`),
                                                    },
                                                    {
                                                        property: 'idSecao',
                                                        operator: 'eq',
                                                        value: getIn(values, `dreMercadologicaConfiguracaoCategorizacao.${index}.secao.idSecao`),
                                                    },
                                                ],
                                            }}
                                        />

                                        <SubgrupoAutoComplete
                                            size="small"
                                            value={estrutura.subgrupo}
                                            error={getIn(errors, `dreMercadologicaConfiguracaoCategorizacao.${index}.subgrupo`)}
                                            optionStringTemplate="<strong>{dsSubgrupo}</strong><br>Grupo {dsGrupo}<br>Seção {dsSecao}<br>Divisão {dsDivisao}"
                                            onChange={(e, value) =>
                                                setFieldValue(`dreMercadologicaConfiguracaoCategorizacao.${index}.subgrupo`, value)
                                            }
                                            requestOptions={{
                                                url: '/gestao/subgrupo',
                                                sort: [{ property: 'dsSubgrupo', direction: 'ASC' }],
                                                columns: 'idSubgrupo,dsSubgrupo,dsGrupo,dsSecao,dsDivisao',
                                                limit: 100,
                                                filter: [
                                                    {
                                                        property: 'idDivisao',
                                                        operator: 'eq',
                                                        value: getIn(values, `dreMercadologicaConfiguracaoCategorizacao.${index}.divisao.idDivisao`),
                                                    },
                                                    {
                                                        property: 'idSecao',
                                                        operator: 'eq',
                                                        value: getIn(values, `dreMercadologicaConfiguracaoCategorizacao.${index}.secao.idSecao`),
                                                    },
                                                    {
                                                        property: 'idGrupo',
                                                        operator: 'eq',
                                                        value: getIn(values, `dreMercadologicaConfiguracaoCategorizacao.${index}.grupo.idGrupo`),
                                                    },
                                                ],
                                            }}
                                        />
                                    </Box>

                                    <IconButton
                                        sx={{ height: 24, width: 24 }}
                                        onClick={() => {
                                            remove(index);

                                            if (isOnly) {
                                                push({});

                                                enqueueSnackbar('É necessário ter ao menos uma estrutura mercadológica', { variant: 'warning' });
                                            }
                                        }}
                                    >
                                        <DeleteOutlineOutlined />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </FieldArray>
    );
}
